import { useCallback, useEffect, useState } from "react";
import type {
  PersonalAccessTokenCreatedResponse,
  PersonalAccessTokenSummary,
} from "@yagni/shared";
import { Button } from "../ui/button.js";
import { Card } from "../ui/card.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../ui/dialog.js";
import { Input } from "../ui/input.js";
import { Label } from "../ui/label.js";
import {
  ChapterSection,
  ChapterLede,
  ChapterCard,
} from "../settings/chapter.js";

interface ListResponse {
  tokens: PersonalAccessTokenSummary[];
}

/**
 * Settings page — create / view / revoke Personal Access Tokens.
 *
 * A PAT lets the user authenticate their local coding agent (Claude Code,
 * Cursor) against YAGNI's `/mcp` endpoint, so the agent can pull tickets,
 * claim them, comment, and drive them through their lifecycle.
 *
 * Plaintext is shown exactly ONCE after creation — we never persist it. The
 * list endpoint only ever returns the 12-char prefix ("yagni_pat_xy") so
 * users can tell their tokens apart.
 */
export function PersonalAccessTokensView() {
  const [tokens, setTokens] = useState<PersonalAccessTokenSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] =
    useState<PersonalAccessTokenCreatedResponse | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch("/api/personal-access-tokens", {
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error(`Failed to load tokens (${res.status})`);
      }
      const data = (await res.json()) as ListResponse;
      setTokens(data.tokens);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load tokens");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleCreate = async (
    name: string,
    expiresAt: string | null,
    scopes: string[] | null,
  ) => {
    setCreating(true);
    setError(null);
    try {
      const res = await fetch("/api/personal-access-tokens", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name,
          expiresAt,
          ...(scopes ? { scopes } : {}),
        }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(data.error ?? `Create failed (${res.status})`);
      }
      const body = (await res.json()) as PersonalAccessTokenCreatedResponse;
      setJustCreated(body);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Create failed");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <ChapterSection label="Access tokens" />
      <ChapterLede>
        Personal Access Tokens let your local coding agent (Claude Code, Cursor,
        and the like) pull YAGNI tickets and drive them through their lifecycle
        via the MCP endpoint. Each token acts on your behalf: comments and phase
        changes made through MCP are attributed to you. Revoked tokens are kept
        for audit but cannot authenticate.
      </ChapterLede>
      <div className="mb-4">
        <CreateTokenButton disabled={creating} onCreate={handleCreate} />
      </div>

      {error && (
        <Card tone="error" padding="sm" className="mb-4 text-sm">
          {error}
        </Card>
      )}

      <ChapterCard className="px-4">
        <div className="divide-y divide-[var(--border-subtle)]">
          {tokens === null && (
            <div className="py-6 text-sm text-[var(--text-tertiary)]">
              Loading…
            </div>
          )}
          {tokens && tokens.length === 0 && (
            <div className="py-6 text-sm text-[var(--text-tertiary)]">
              No tokens yet. Create one to connect Claude Code or Cursor.
            </div>
          )}
          {tokens?.map((token) => (
            <TokenRow
              key={token.id}
              token={token}
              onRevoked={() => void refresh()}
            />
          ))}
        </div>
      </ChapterCard>

      {justCreated && (
        <TokenCreatedDialog
          data={justCreated}
          onClose={() => setJustCreated(null)}
        />
      )}
    </div>
  );
}

const EXPIRY_OPTIONS: { label: string; days: number | null }[] = [
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "1 year", days: 365 },
  { label: "Never", days: null },
];

function CreateTokenButton(props: {
  disabled: boolean;
  onCreate: (
    name: string,
    expiresAt: string | null,
    scopes: string[] | null,
  ) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [expiryDays, setExpiryDays] = useState<number | null>(90);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)} disabled={props.disabled}>
        Create token
      </Button>
      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent>
          <DialogTitle>Create personal access token</DialogTitle>
          <DialogDescription>
            Pick a recognizable name so you can tell your tokens apart later.
            You'll see the plaintext exactly once, so save it somewhere safe.
          </DialogDescription>

          <div className="mt-4">
            <Label htmlFor="pat-name">Name</Label>
            <Input
              id="pat-name"
              placeholder="e.g. My laptop, Claude Code"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={busy}
              maxLength={80}
            />
          </div>

          <div className="mt-4">
            <Label htmlFor="pat-expiry">Expires</Label>
            <select
              id="pat-expiry"
              data-testid="pat-expiry-select"
              value={expiryDays === null ? "never" : String(expiryDays)}
              onChange={(e) => {
                const v = e.target.value;
                setExpiryDays(v === "never" ? null : Number(v));
              }}
              disabled={busy}
              className="mt-1 block w-full rounded-md border border-[var(--border)] bg-[var(--bg-input)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--border-focus)]"
            >
              {EXPIRY_OPTIONS.map((o) => (
                <option
                  key={o.label}
                  value={o.days === null ? "never" : String(o.days)}
                >
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-6 flex gap-2 justify-end">
            <Button
              variant="secondary"
              onClick={() => setOpen(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!name.trim()) return;
                setBusy(true);
                try {
                  const expiresAt =
                    expiryDays === null
                      ? null
                      : new Date(
                          Date.now() + expiryDays * 24 * 60 * 60 * 1000,
                        ).toISOString();
                  await props.onCreate(name.trim(), expiresAt, null);
                  setOpen(false);
                  setName("");
                  setExpiryDays(90);
                } finally {
                  setBusy(false);
                }
              }}
              disabled={busy || name.trim().length === 0}
              loading={busy}
            >
              Create
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function TokenRow(props: {
  token: PersonalAccessTokenSummary;
  onRevoked: () => void;
}) {
  const { token } = props;
  const [busy, setBusy] = useState(false);
  const revoked = token.revokedAt !== null;

  return (
    <div className="flex items-center justify-between py-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">{token.name}</span>
          {token.scopes?.map((scope) => (
            <span
              key={scope}
              className="text-xs px-2 py-0.5 rounded bg-[var(--border-subtle)] text-[var(--text-secondary)] font-mono"
            >
              {scope}
            </span>
          ))}
          {revoked && (
            <span className="text-xs px-2 py-0.5 rounded bg-[var(--border-subtle)] text-[var(--text-tertiary)]">
              revoked
            </span>
          )}
        </div>
        <div className="text-sm text-[var(--text-secondary)]">
          <code>{token.tokenPrefix}…</code>
          <span className="mx-2">·</span>
          created {formatDate(token.createdAt)}
          {token.lastUsedAt && (
            <>
              <span className="mx-2">·</span>
              last used {formatDate(token.lastUsedAt)}
            </>
          )}
          {token.expiresAt && (
            <>
              <span className="mx-2">·</span>
              expires {formatDate(token.expiresAt)}
            </>
          )}
        </div>
      </div>
      {!revoked && (
        <Button
          variant="secondary"
          size="sm"
          disabled={busy}
          loading={busy}
          onClick={async () => {
            if (!confirm(`Revoke "${token.name}"? Any client using this token will stop working immediately.`)) {
              return;
            }
            setBusy(true);
            try {
              await fetch(`/api/personal-access-tokens/${token.id}`, {
                method: "DELETE",
                credentials: "include",
              });
            } finally {
              setBusy(false);
              props.onRevoked();
            }
          }}
        >
          Revoke
        </Button>
      )}
    </div>
  );
}

function TokenCreatedDialog(props: {
  data: PersonalAccessTokenCreatedResponse;
  onClose: () => void;
}) {
  const { plaintext, token } = props.data;
  const snippet = `claude mcp add --transport http yagni ${window.location.origin}/mcp --header "Authorization: Bearer ${plaintext}"`;
  return (
    <Dialog open={true} onOpenChange={(v) => !v && props.onClose()}>
      <DialogContent>
        <DialogTitle>Token created: copy it now</DialogTitle>
        <DialogDescription>
          This is the only time you'll see the plaintext for "{token.name}".
          Save it somewhere safe, or paste the setup snippet below straight
          into your shell.
        </DialogDescription>

        <div className="mt-4">
          <Label>Token</Label>
          <div className="flex items-center gap-2 mt-1">
            <Input readOnly value={plaintext} className="font-mono text-xs" />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void navigator.clipboard.writeText(plaintext)}
            >
              Copy
            </Button>
          </div>
        </div>

        <div className="mt-4">
          <Label>Claude Code setup</Label>
          <div className="flex items-start gap-2 mt-1">
            <pre className="flex-1 text-xs bg-[var(--bg-code)] p-3 rounded border border-[var(--print-border)] whitespace-pre-wrap break-all font-mono">
              {snippet}
            </pre>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void navigator.clipboard.writeText(snippet)}
            >
              Copy
            </Button>
          </div>
        </div>

        <div className="mt-6 flex gap-2 justify-end">
          <Button onClick={props.onClose}>Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}
